'use client'

import { useState, useMemo, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { analytics } from '@/lib/analytics'
import { SearchInput } from '@/components/ui/Input'
import { ComponentLoader } from '@/components/ui/Loading'
import StationeryDesignCard, {
  type StationeryDesign,
} from './StationeryDesignCard'
import StationeryDesignPreviewContainer from './StationeryDesignPreviewContainer'

interface StationeryDesignGridProps {
  /** Callback when designs are loaded (for parent to track count) */
  onDesignsLoaded?: (count: number) => void
}

export default function StationeryDesignGrid({
  onDesignsLoaded,
}: StationeryDesignGridProps) {
  const router = useRouter()
  const t = useTranslations('designs')
  const [searchQuery, setSearchQuery] = useState('')
  const [previewDesign, setPreviewDesign] = useState<StationeryDesign | null>(
    null
  )
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [designs, setDesigns] = useState<StationeryDesign[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Track page view
  const hasTrackedPageView = useRef(false)

  // Dynamically load stationery demos when component mounts
  useEffect(() => {
    import('@kiltechco/dday-templates/stationery/demos').then((module) => {
      // STATIONERY_DEMOS is an array of demos
      const demoArray = module.STATIONERY_DEMOS as StationeryDesign[]
      setDesigns(demoArray)
      setIsLoading(false)
      onDesignsLoaded?.(demoArray.length)

      // Track page view after designs are loaded
      if (!hasTrackedPageView.current) {
        hasTrackedPageView.current = true
        analytics.page.designsPageViewed(demoArray.length)
      }
    })
  }, [onDesignsLoaded])

  // Convert designs to suggestions for autocomplete
  const searchSuggestions = useMemo(
    () => designs.map((design) => ({ id: design.id, label: design.label })),
    [designs]
  )

  // Filter designs based on search query
  const filteredDesigns = useMemo(() => {
    if (!searchQuery.trim()) return designs

    const query = searchQuery.toLowerCase().trim()
    return designs.filter(
      (design) =>
        design.id.toLowerCase().includes(query) ||
        design.label.toLowerCase().includes(query) ||
        design.description?.toLowerCase().includes(query)
    )
  }, [searchQuery, designs])

  const handleDesignSelect = (design: StationeryDesign) => {
    // Track template selection
    analytics.builder.templateSelected(design.id, design.label, 'stationery')
    // Navigate to stationery builder with designId
    router.push(`/stationery-builder?designId=${design.id}`)
  }

  const handleDesignPreview = (design: StationeryDesign) => {
    // Track design preview
    analytics.page.designPreviewed(design.id, design.label)
    setPreviewDesign(design)
    setIsPreviewOpen(true)
  }

  const handleSearchSelect = (suggestion: { id: string; label: string }) => {
    // Track search selection
    analytics.search.performed(suggestion.label, 1, 'stationery-designs')
    // Just update the search query to filter the grid
    setSearchQuery(suggestion.label)
  }

  // Track search when user types (debounced effect)
  const lastSearchRef = useRef('')
  useEffect(() => {
    if (
      searchQuery &&
      searchQuery !== lastSearchRef.current &&
      filteredDesigns.length >= 0
    ) {
      lastSearchRef.current = searchQuery
      // Only track if user has typed at least 2 characters
      if (searchQuery.length >= 2) {
        analytics.search.performed(
          searchQuery,
          filteredDesigns.length,
          'stationery-designs'
        )
      }
    }
  }, [searchQuery, filteredDesigns.length])

  return (
    <>
      {/* Search Bar with Autocomplete */}
      <div className="mb-6">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('searchPlaceholder')}
          className="max-w-md mx-auto"
          suggestions={searchSuggestions}
          onSelect={handleSearchSelect}
        />
      </div>

      {/* Designs Grid */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <ComponentLoader size="lg" />
          <p className="text-muted-foreground text-sm">{t('loading')}</p>
        </div>
      ) : filteredDesigns.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {filteredDesigns.map((design, index) => (
            <StationeryDesignCard
              key={design.id}
              design={design}
              onClick={handleDesignSelect}
              onPreview={handleDesignPreview}
              priority={index < 5}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-10 sm:py-12 px-4">
          <p className="text-muted-foreground text-base sm:text-lg mb-3 sm:mb-4">
            {t('noDesignsFound')}
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="text-primary hover:underline font-medium text-sm sm:text-base cursor-pointer"
          >
            {t('clearSearch')}
          </button>
        </div>
      )}

      {/* Preview Drawer */}
      <StationeryDesignPreviewContainer
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        design={previewDesign}
        onSelect={handleDesignSelect}
      />
    </>
  )
}
